import { useState, useEffect } from "react";
import { useApp, api } from "./context";

// Day / month / year pickers avoid the native date input opening on today's
// date, which makes choosing a birth year painfully slow on mobile.
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const GENDERS = ["Male", "Female", "Non-binary", "Prefer not to say"];

const pad = (n) => String(n).padStart(2, "0");

const daysInMonth = (year, month) => {
  if (!year || !month) return 31;
  return new Date(Number(year), Number(month), 0).getDate();
};

export const AgeGate = ({ onVerified }) => {
  const { dark } = useApp();

  const [day, setDay] = useState("");
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [gender, setGender] = useState("");
  const [country, setCountry] = useState("Kenya");
  const [county, setCounty] = useState("");
  const [countryGroups, setCountryGroups] = useState([]);
  const [counties, setCounties] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [blocked, setBlocked] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api("/age/options");
        if (!alive || !res.ok) return;
        if (Array.isArray(res.data?.counties)) setCounties(res.data.counties);
        if (Array.isArray(res.data?.countries)) setCountryGroups(res.data.countries);
      } catch (e) {
        // Non-fatal: the form still works, the lists just stay empty
      }
    })();
    return () => { alive = false; };
  }, []);

  const isKenya = country === "Kenya";

  const thisYear = new Date().getFullYear();
  const years = [];
  for (let y = thisYear - 18; y >= thisYear - 100; y--) years.push(y);

  const days = [];
  for (let d = 1; d <= daysInMonth(year, month); d++) days.push(d);

  const complete = day && month && year && gender && country && (!isKenya || county);

  const submit = async () => {
    if (!complete || saving) return;
    setError("");
    setSaving(true);
    try {
      const dob = `${year}-${pad(month)}-${pad(day)}`;
      const res = await api("/age/verify", {
        method: "POST",
        body: JSON.stringify({
          date_of_birth: dob,
          gender,
          country,
          county: isKenya ? county : null,
        }),
      });
      if (res.ok && res.data?.ok) {
        if (onVerified) onVerified(res.data);
        return;
      }
      if (res.data?.underage) {
        setBlocked(true);
        setError(res.data.error || "You must be 18 or older to use this platform.");
      } else {
        setError(res.data?.error || "Could not save. Please check your details.");
      }
    } catch (e) {
      setError("Network problem. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const bg = dark ? "#14162b" : "#ffffff";
  const text = dark ? "#f2f2f7" : "#1a1a2e";
  const sub = dark ? "#9aa0b5" : "#6b7280";
  const border = dark ? "#2a2d48" : "#e5e7eb";
  const fieldBg = dark ? "#1e2138" : "#f8f8fb";

  const label = {
    fontSize: 12,
    fontWeight: 700,
    color: sub,
    marginBottom: 6,
    display: "block",
    letterSpacing: 0.3,
  };

  const select = {
    width: "100%",
    padding: "11px 10px",
    borderRadius: 10,
    border: "1px solid " + border,
    background: fieldBg,
    color: text,
    fontSize: 14,
    fontFamily: "inherit",
    outline: "none",
    appearance: "none",
    cursor: "pointer",
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 99999,
        background: "rgba(6,8,20,0.88)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        overflowY: "auto",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 430,
          background: bg,
          borderRadius: 20,
          border: "1px solid " + border,
          padding: 24,
          boxShadow: "0 24px 60px rgba(0,0,0,0.45)",
          maxHeight: "92vh",
          overflowY: "auto",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: "50%",
              margin: "0 auto 12px",
              background: "linear-gradient(135deg,#6c5ce7,#e91e8c)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 24,
            }}
          >
            🔞
          </div>
          <div style={{ fontSize: 19, fontWeight: 800, color: text, marginBottom: 6 }}>
            Confirm your age
          </div>
          <div style={{ fontSize: 13, color: sub, lineHeight: 1.5 }}>
            This is an adult platform. We need to confirm you are 18 or older
            before you continue.
          </div>
        </div>

        {blocked ? (
          <div
            style={{
              padding: 18,
              borderRadius: 14,
              background: dark ? "#3a1520" : "#fee2e2",
              border: "1px solid #ef4444",
              color: dark ? "#fca5a5" : "#991b1b",
              fontSize: 14,
              lineHeight: 1.6,
              textAlign: "center",
            }}
          >
            {error}
            <div style={{ fontSize: 12, marginTop: 10, opacity: 0.85 }}>
              You cannot access this platform. Please close this page.
            </div>
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 16 }}>
              <span style={label}>DATE OF BIRTH</span>
              <div style={{ display: "flex", gap: 8 }}>
                <select
                  value={day}
                  onChange={(e) => setDay(e.target.value)}
                  style={{ ...select, flex: "0 0 78px" }}
                >
                  <option value="">Day</option>
                  {days.map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
                <select
                  value={month}
                  onChange={(e) => setMonth(e.target.value)}
                  style={{ ...select, flex: 1 }}
                >
                  <option value="">Month</option>
                  {MONTHS.map((m, i) => (
                    <option key={m} value={i + 1}>{m}</option>
                  ))}
                </select>
                <select
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  style={{ ...select, flex: "0 0 92px" }}
                >
                  <option value="">Year</option>
                  {years.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
              <div
                style={{
                  marginTop: 10,
                  padding: "10px 12px",
                  borderRadius: 10,
                  background: dark ? "#3a2e12" : "#fef3c7",
                  border: "1px solid " + (dark ? "#7c5f16" : "#fcd34d"),
                  color: dark ? "#fcd34d" : "#92400e",
                  fontSize: 12,
                  lineHeight: 1.6,
                }}
              >
                <strong>Please get this right.</strong> Your age is used to match
                you with the right people and to keep the platform safe. Once you
                confirm, <strong>your date of birth cannot be changed</strong> and
                will be treated as the truth.
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <span style={label}>I AM</span>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {GENDERS.map((g) => {
                  const on = gender === g;
                  return (
                    <button
                      key={g}
                      onClick={() => setGender(g)}
                      style={{
                        flex: "1 1 46%",
                        padding: "10px 8px",
                        borderRadius: 10,
                        border: on ? "1px solid transparent" : "1px solid " + border,
                        background: on
                          ? "linear-gradient(135deg,#6c5ce7,#e91e8c)"
                          : fieldBg,
                        color: on ? "#fff" : text,
                        fontSize: 13,
                        fontWeight: 700,
                        cursor: "pointer",
                        fontFamily: "inherit",
                      }}
                    >
                      {g}
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={{ marginBottom: isKenya ? 14 : 18 }}>
              <span style={label}>WHICH COUNTRY ARE YOU IN?</span>
              <select
                value={country}
                onChange={(e) => { setCountry(e.target.value); setCounty(""); }}
                style={select}
              >
                {countryGroups.length === 0 ? (
                  <option value="Kenya">Kenya</option>
                ) : (
                  countryGroups.map((grp) => (
                    <optgroup key={grp.group} label={grp.group}>
                      {grp.items.map((name) => (
                        <option key={name} value={name}>{name}</option>
                      ))}
                    </optgroup>
                  ))
                )}
              </select>
            </div>

            {isKenya ? (
              <div style={{ marginBottom: 18 }}>
                <span style={label}>WHICH COUNTY?</span>
                <select
                  value={county}
                  onChange={(e) => setCounty(e.target.value)}
                  style={select}
                >
                  <option value="">Select your county</option>
                  {counties.map((x) => (
                    <option key={x.code} value={x.name}>{x.name}</option>
                  ))}
                </select>
              </div>
            ) : null}

            {error ? (
              <div
                style={{
                  padding: "10px 12px",
                  borderRadius: 10,
                  background: dark ? "#3a1520" : "#fee2e2",
                  color: dark ? "#fca5a5" : "#991b1b",
                  fontSize: 13,
                  marginBottom: 14,
                }}
              >
                {error}
              </div>
            ) : null}

            <button
              onClick={submit}
              disabled={!complete || saving}
              style={{
                width: "100%",
                padding: "13px",
                borderRadius: 12,
                border: "none",
                background: complete
                  ? "linear-gradient(135deg,#6c5ce7,#e91e8c)"
                  : dark ? "#2a2d48" : "#e5e7eb",
                color: complete ? "#fff" : sub,
                fontSize: 15,
                fontWeight: 800,
                cursor: complete && !saving ? "pointer" : "not-allowed",
                fontFamily: "inherit",
              }}
            >
              {saving ? "Saving..." : "Confirm and continue"}
            </button>

            <div
              style={{
                fontSize: 11,
                color: sub,
                textAlign: "center",
                marginTop: 12,
                lineHeight: 1.5,
              }}
            >
              Your date of birth is private and never shown on your profile.
              Only your general area is used to find people near you.
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AgeGate;